import { type RefObject, useLayoutEffect, useRef, useState } from "react";

/** Geometry of the active item relative to its container's padding box. */
export interface IndicatorRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface SlidingIndicator<T extends HTMLElement> {
  /** Attach to the element that positions the indicator (must be positioned). */
  containerRef: RefObject<T>;
  /** Geometry to animate the indicator to, or null before the first measure. */
  indicator: IndicatorRect | null;
}

/**
 * Measures the active descendant within a container so a single indicator
 * element can slide and resize to cover it. `activeSelector` defaults to the
 * data attribute used by Segmented and can be changed for wrapped primitives.
 *
 * This deliberately replaces a shared-layout `layoutId` animation: because the
 * indicator is measured and animated locally (plain `x`/`y`/`width`/`height`),
 * it never participates in `AnimatePresence` layout projection, and therefore
 * cannot hang a `mode="wait"` exit and strand the incoming screen on the blank
 * shell canvas. Re-measures when the active item (`activeKey`) changes and
 * whenever the container resizes (responsive/fluid widths, wrapping, fonts).
 */
export function useSlidingIndicator<T extends HTMLElement>(
  activeKey: string,
  activeSelector = '[data-active="true"]',
): SlidingIndicator<T> {
  const containerRef = useRef<T>(null);
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeKey isn't read in the body, but it's the trigger that must force a re-measure when the selection changes (the new active element only gets `data-active="true"` after this render commits).
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const measure = () => {
      const active =
        containerRef.current?.querySelector<HTMLElement>(activeSelector);
      if (!active) {
        setIndicator(null);
        return;
      }
      // offsetLeft/offsetTop are measured from the offsetParent's padding edge,
      // which is exactly the origin CSS `left`/`top` (and thus `x`/`y`) use, so
      // the indicator aligns without any border correction.
      const next: IndicatorRect = {
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      };
      setIndicator((prev) =>
        prev &&
        prev.left === next.left &&
        prev.top === next.top &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next,
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [activeKey, activeSelector]);

  return { containerRef, indicator };
}
